/**
 * The genre vocabulary: the canonical list, its token colours, and the Typeahead source built
 * from it.
 *
 * Lifted out of StoryOnboarding so the data is not welded to the component that happens to
 * introduce it. Two things follow from that. App.tsx reaches the search source and the
 * label->item lookup without importing a StyleX module, which the client test project cannot
 * compile; and StoryOnboarding keeps Fast Refresh, which a module exporting both a component
 * and plain helpers loses.
 */
import { type TokenColor } from '@astryxdesign/core/Token'
import { createStaticSource, type SearchableItem } from '@astryxdesign/core/Typeahead'

interface GenreInfo {
  name: string
  description: string
}

export interface GenreAuxData {
  description: string
  color: TokenColor
}

export type GenreItem = SearchableItem<GenreAuxData>

// Groups genres by thematic family so tokens carry meaningful, scannable color.
const GENRE_FAMILY_COLOR: Record<string, TokenColor> = {
  'Literary Fiction': 'green',
  'Historical Fiction': 'green',
  Adventure: 'green',
  Contemporary: 'green',
  'Coming-of-Age': 'green',
  'Family Saga': 'green',
  'Slice of Life': 'green',
  'Science Fiction': 'blue',
  Dystopian: 'blue',
  Cyberpunk: 'blue',
  'Space Opera': 'blue',
  Steampunk: 'blue',
  'Post-Apocalyptic': 'blue',
  'Time Travel': 'blue',
  'Alternate History': 'blue',
  LitRPG: 'blue',
  Fantasy: 'purple',
  'Urban Fantasy': 'purple',
  'Magical Realism': 'purple',
  'Mythology & Folk': 'purple',
  'High Fantasy': 'purple',
  'Cozy Fantasy': 'purple',
  'Sword & Sorcery': 'purple',
  'Fairy Tale Retelling': 'purple',
  'Portal Fantasy': 'purple',
  Horror: 'red',
  Thriller: 'red',
  Grimdark: 'red',
  'Psychological Thriller': 'red',
  'Crime / Detective': 'red',
  Noir: 'red',
  Splatterpunk: 'red',
  Romance: 'pink',
  'Paranormal Romance': 'pink',
  'Chick Lit': 'pink',
  Mystery: 'teal',
  'Cozy Mystery': 'teal',
  'Spy / Espionage': 'teal',
  'Legal Thriller': 'teal',
  'Comedy / Humor': 'yellow',
  Satire: 'yellow',
  'Absurdist Fiction': 'yellow',
  'Young Adult': 'cyan',
  'New Adult': 'cyan',
  'Middle Grade': 'cyan',
  "Children's": 'cyan',
  Western: 'orange',
  War: 'orange',
  Survival: 'orange',
  Heist: 'orange',
  Gothic: 'gray',
  'Weird Fiction': 'gray',
  Supernatural: 'gray',
  Paranormal: 'gray'
}

const GENRES: GenreInfo[] = [
  {
    name: 'Literary Fiction',
    description: 'Character-driven, explores complex themes and relationships.'
  },
  {
    name: 'Science Fiction',
    description: 'Future settings, technology, space travel, often explores societal issues.'
  },
  {
    name: 'Fantasy',
    description: 'Magic, mythical creatures, often set in medieval or secondary worlds.'
  },
  {
    name: 'Horror',
    description: 'Aims to scare, evoke dread, often features supernatural elements.'
  },
  { name: 'Mystery', description: 'Focuses on solving a crime or puzzle.' },
  { name: 'Thriller', description: 'Suspenseful, fast-paced, often features danger and action.' },
  { name: 'Romance', description: 'Central theme is a love story.' },
  {
    name: 'Historical Fiction',
    description: 'Set in the past, often incorporates real historical events or figures.'
  },
  {
    name: 'Adventure',
    description: 'Quests, exploration, and journeys full of action and survival.'
  },
  {
    name: 'Dystopian',
    description: 'Features a bleak future society, often explores social and political control.'
  },
  { name: 'Cyberpunk', description: 'High-tech and low-life, set in futuristic urban societies.' },
  {
    name: 'Space Opera',
    description: 'Large-scale space travel, galactic warfare, and epic melodrama.'
  },
  {
    name: 'Urban Fantasy',
    description: 'Magic and supernatural elements integrated into modern city settings.'
  },
  {
    name: 'Magical Realism',
    description: 'Magical elements blended naturally into a realistic, mundane world.'
  },
  {
    name: 'Steampunk',
    description: 'Alternate histories featuring steam-powered industrial technology.'
  },
  { name: 'Grimdark', description: 'Dark, gritty, morally ambiguous settings with high stakes.' },
  {
    name: 'Contemporary',
    description: 'Set in the present day, deals with modern issues and relationships.'
  },
  {
    name: 'Comedy / Humor',
    description: 'Humorous, satirical, lighthearted stories designed to entertain.'
  },
  {
    name: 'Mythology & Folk',
    description: 'Inspired by ancient gods, legends, folklore, and cultural myths.'
  },
  {
    name: 'Young Adult',
    description: 'Focuses on teenage experiences and coming-of-age journeys.'
  },
  {
    name: 'High Fantasy',
    description: 'Epic, large-scale world-building and magic systems with grand stakes.'
  },
  {
    name: 'Post-Apocalyptic',
    description: 'Survival and society in the aftermath of a global catastrophe.'
  },
  {
    name: 'Psychological Thriller',
    description: "Intense suspense centered around characters' complex mental and emotional states."
  },
  {
    name: 'Cozy Fantasy',
    description: 'Low-stakes, comforting fantasy settings focusing on community and daily life.'
  },
  {
    name: 'Crime / Detective',
    description: 'Focuses on investigators, criminals, investigations, and solving offenses.'
  },
  {
    name: 'Coming-of-Age',
    description: 'A young protagonist grows up and discovers who they are.'
  },
  {
    name: 'Family Saga',
    description: "Multi-generational stories following one family's triumphs and troubles."
  },
  {
    name: 'Slice of Life',
    description: 'Everyday moments and relationships, told with quiet realism.'
  },
  {
    name: 'Time Travel',
    description: 'Characters journey across timelines, altering or witnessing history.'
  },
  {
    name: 'Alternate History',
    description: 'Real history reimagined with a pivotal change in the past.'
  },
  {
    name: 'LitRPG',
    description: "Game-like stats, levels, and systems drive the story's progression."
  },
  {
    name: 'Sword & Sorcery',
    description: 'Gritty, action-driven fantasy centered on daring heroes and duels.'
  },
  {
    name: 'Fairy Tale Retelling',
    description: 'A classic fairy tale reimagined with a fresh twist.'
  },
  {
    name: 'Portal Fantasy',
    description: 'Characters cross from the ordinary world into a magical one.'
  },
  {
    name: 'Noir',
    description: 'Cynical, shadow-drenched crime stories with morally grey characters.'
  },
  {
    name: 'Splatterpunk',
    description: 'Visceral, graphic horror that pushes past traditional restraint.'
  },
  {
    name: 'Paranormal Romance',
    description: 'Romance entwined with vampires, shifters, or other supernatural beings.'
  },
  { name: 'Chick Lit', description: 'Witty, relationship-focused stories about modern womanhood.' },
  {
    name: 'Cozy Mystery',
    description: 'Low-violence whodunits in charming settings with amateur sleuths.'
  },
  {
    name: 'Spy / Espionage',
    description: 'Secret agents, tradecraft, and international intrigue.'
  },
  {
    name: 'Legal Thriller',
    description: 'Courtroom drama and legal maneuvering under high stakes.'
  },
  {
    name: 'Satire',
    description: 'Sharp social or political commentary delivered through irony and exaggeration.'
  },
  {
    name: 'Absurdist Fiction',
    description: 'Illogical, surreal scenarios that defy conventional reasoning.'
  },
  { name: 'New Adult', description: 'Stories following characters navigating early adulthood.' },
  { name: 'Middle Grade', description: 'Age-appropriate adventures written for pre-teen readers.' },
  { name: "Children's", description: 'Gentle, imaginative stories written for young children.' },
  { name: 'Western', description: 'Frontier settings, outlaws, cowboys, and rugged survival.' },
  {
    name: 'War',
    description: 'Combat and its human cost, set during historical or fictional conflicts.'
  },
  {
    name: 'Survival',
    description: 'Characters fighting to endure hostile environments or disasters.'
  },
  { name: 'Heist', description: 'A crew plans and executes an elaborate theft or con.' },
  { name: 'Gothic', description: 'Atmospheric dread, crumbling estates, and romantic melancholy.' },
  {
    name: 'Weird Fiction',
    description: 'Unsettling, genre-blurring stories built on the inexplicable.'
  },
  {
    name: 'Supernatural',
    description: 'Ghosts, hauntings, and otherworldly forces intrude on reality.'
  },
  { name: 'Paranormal', description: 'Unexplained phenomena and hidden supernatural worlds.' }
]
export const GENRE_NAMES = GENRES.map((genre) => genre.name)

export const GENRE_ITEMS: GenreItem[] = GENRES.map((genre) => ({
  id: genre.name,
  label: genre.name,
  auxiliaryData: {
    description: genre.description,
    color: GENRE_FAMILY_COLOR[genre.name] ?? 'default'
  }
}))
export const GENRE_SEARCH_SOURCE = createStaticSource(GENRE_ITEMS)

export const genreItemFromLabel = (label: string): GenreItem =>
  GENRE_ITEMS.find((item) => item.label === label) ?? {
    id: label.toLowerCase().replace(/[^a-z0-9]+/g, '-') || label,
    label
  }

/** Words that mean "no genre" rather than a genre; older rows can carry a stringified null. */
const UNSET_GENRE_WORDS = new Set(['null', 'undefined'])

/**
 * The tokens for a story's stored genre string ("Fantasy, Horror"). Tolerates null, undefined,
 * and the literal text "null" that an old save bug left in some stories, so an unset genre
 * always yields an empty list rather than a token labelled "null".
 */
export const genreTokens = (genre?: string | null): GenreItem[] =>
  String(genre || '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value && !UNSET_GENRE_WORDS.has(value.toLowerCase()))
    .slice(0, 5)
    .map(genreItemFromLabel)
